import { useState, useMemo, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import api from '@/api/client'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { ArrowLeft, Save, Trash2, Search, Check } from 'lucide-react'
import { HelpBanner } from '@/components/ui/help-banner'
import type { RoleResponse, ProductResponse, PaginatedResponse } from '@/api/types'

interface PermissionEntry {
  id: number
  code: string
  description?: string
}

interface PermissionCatalogEntry {
  product_id: number
  permissions: PermissionEntry[]
}

function groupByDomain(permissions: PermissionEntry[]): Record<string, PermissionEntry[]> {
  const groups: Record<string, PermissionEntry[]> = {}
  for (const p of permissions) {
    const parts = p.code.split('.')
    const domain = parts.length >= 2 ? parts[1] : 'other'
    if (!groups[domain]) groups[domain] = []
    groups[domain].push(p)
  }
  return groups
}

export function RoleDetailPage() {
  const { t } = useTranslation(['roles', 'common'])
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const roleId = Number(id)

  const [editName, setEditName] = useState<string | null>(null)
  const [permSearch, setPermSearch] = useState('')
  const [selectedPermIds, setSelectedPermIds] = useState<Set<number>>(new Set())
  const [permsDirty, setPermsDirty] = useState(false)
  const [deleteOpen, setDeleteOpen] = useState(false)

  // --- Fetch role ---
  const { data: roleData, isLoading } = useQuery({
    queryKey: ['role', roleId],
    queryFn: () => api.get<RoleResponse>(`/roles/${roleId}`),
    enabled: !!roleId,
  })
  const role = roleData?.data

  // --- Fetch products ---
  const { data: productsData } = useQuery({
    queryKey: ['products-all'],
    queryFn: () => api.get<PaginatedResponse<ProductResponse>>('/products?offset=0&limit=100'),
  })
  const productLookup = useMemo(() => {
    const map: Record<number, ProductResponse> = {}
    for (const p of productsData?.data.data ?? []) map[p.id] = p
    return map
  }, [productsData])

  // --- Fetch permission catalog ---
  const { data: catalogData } = useQuery({
    queryKey: ['permissions'],
    queryFn: () => api.get<PermissionCatalogEntry[]>('/permissions'),
  })

  // Initialize selected permissions from role data
  const rolePerms = role?.permissions ?? []
  useEffect(() => {
    if (role && !permsDirty) {
      setSelectedPermIds(new Set(rolePerms.map((p) => p.id)))
    }
  }, [role, permsDirty])

  // Available permissions for this role's product
  const availablePerms = useMemo(() => {
    if (!role?.product_id || !catalogData?.data) return []
    const group = catalogData.data.find((g) => g.product_id === role.product_id)
    return group?.permissions ?? []
  }, [catalogData, role?.product_id])

  const filteredPerms = useMemo(() => {
    if (!permSearch.trim()) return availablePerms
    const q = permSearch.toLowerCase()
    return availablePerms.filter(
      (p) => p.code.toLowerCase().includes(q) || p.description?.toLowerCase().includes(q)
    )
  }, [availablePerms, permSearch])

  const domains = useMemo(() => groupByDomain(filteredPerms), [filteredPerms])

  // --- Mutations ---
  const updateNameMutation = useMutation({
    mutationFn: (name: string) => api.put(`/roles/${roleId}`, { name }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['role', roleId] })
      queryClient.invalidateQueries({ queryKey: ['roles'] })
      setEditName(null)
    },
  })

  const bindPermsMutation = useMutation({
    mutationFn: (permissionIds: number[]) =>
      api.post(`/roles/${roleId}/permissions`, { permission_ids: permissionIds }),
  })

  const unbindPermMutation = useMutation({
    mutationFn: (permissionId: number) =>
      api.delete(`/roles/${roleId}/permissions/${permissionId}`),
  })

  const deleteMutation = useMutation({
    mutationFn: () => api.delete(`/roles/${roleId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['roles'] })
      navigate('/roles')
    },
  })

  const [saving, setSaving] = useState(false)

  async function savePermissions() {
    if (!role) return
    setSaving(true)
    try {
      const currentIds = new Set((role.permissions ?? []).map((p) => p.id))
      const toAdd = [...selectedPermIds].filter((id) => !currentIds.has(id))
      const toRemove = [...currentIds].filter((id) => !selectedPermIds.has(id))

      // Unbind removed permissions
      for (const permId of toRemove) {
        await unbindPermMutation.mutateAsync(permId)
      }
      // Bind new permissions
      if (toAdd.length > 0) {
        await bindPermsMutation.mutateAsync(toAdd)
      }

      await queryClient.invalidateQueries({ queryKey: ['role', roleId] })
      setPermsDirty(false)
    } finally {
      setSaving(false)
    }
  }

  function togglePermission(permId: number) {
    setSelectedPermIds((prev) => {
      const next = new Set(prev)
      if (next.has(permId)) next.delete(permId)
      else next.add(permId)
      return next
    })
    setPermsDirty(true)
  }

  function selectAll() {
    setSelectedPermIds(new Set(availablePerms.map((p) => p.id)))
    setPermsDirty(true)
  }

  function deselectAll() {
    setSelectedPermIds(new Set())
    setPermsDirty(true)
  }

  if (isLoading) return <p className="text-muted-foreground p-6">{t('loading', { ns: 'common' })}</p>
  if (!role) return <p className="text-muted-foreground p-6">{t('notFound')}</p>

  const product = role.product_id ? productLookup[role.product_id] : null

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate('/roles')}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h2 className="text-2xl font-semibold">{role.name}</h2>
            <div className="flex items-center gap-2 mt-1">
              <Badge variant="outline">{role.scope_level}</Badge>
              {product && <Badge variant="secondary">{product.name}</Badge>}
              <span className="text-xs text-muted-foreground">
                {t('permissionCount', { count: selectedPermIds.size })}
              </span>
            </div>
          </div>
        </div>
        <Button variant="destructive" size="sm" onClick={() => setDeleteOpen(true)}>
          <Trash2 className="h-4 w-4 mr-1" />{t('buttons.delete', { ns: 'common' })}
        </Button>
      </div>

      <HelpBanner storageKey="role-detail">
        <p>{t('detail.help.line1')}</p>
        <p>{t('detail.help.line2')}</p>
      </HelpBanner>

      {/* Role info card */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">{t('detail.title')}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="text-xs text-muted-foreground">{t('labels.name', { ns: 'common' })}</Label>
              {editName !== null ? (
                <div className="flex gap-2 mt-1">
                  <Input
                    value={editName}
                    onChange={(e) => setEditName(e.target.value)}
                    className="h-8"
                  />
                  <Button
                    size="sm"
                    onClick={() => editName && updateNameMutation.mutate(editName)}
                    disabled={updateNameMutation.isPending || !editName.trim()}
                  >
                    <Save className="h-3.5 w-3.5" />
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => setEditName(null)}>
                    {t('buttons.cancel', { ns: 'common' })}
                  </Button>
                </div>
              ) : (
                <p
                  className="mt-1 cursor-pointer hover:text-muted-foreground transition-colors"
                  onClick={() => setEditName(role.name)}
                >
                  {role.name}
                </p>
              )}
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">{t('scopeLevel')}</Label>
              <p className="mt-1"><Badge variant="outline">{role.scope_level}</Badge></p>
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">{t('labels.product', { ns: 'common' })}</Label>
              <p className="mt-1">{product ? <Badge variant="secondary">{product.name} ({product.code})</Badge> : <span className="text-muted-foreground">—</span>}</p>
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">{t('labels.created', { ns: 'common' })}</Label>
              <p className="mt-1 text-sm text-muted-foreground">{new Date(role.created_at).toLocaleString()}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Permissions card */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-base">
              {t('permissions')}
              <Badge variant="secondary" className="ml-2 text-xs">
                {selectedPermIds.size} / {availablePerms.length}
              </Badge>
            </CardTitle>
            <div className="flex items-center gap-2">
              {permsDirty && (
                <Button size="sm" onClick={savePermissions} disabled={saving}>
                  <Save className="h-3.5 w-3.5 mr-1" />
                  {saving ? t('saving', { ns: 'common' }) : t('buttons.saveChanges', { ns: 'common' })}
                </Button>
              )}
              <Button variant="outline" size="sm" onClick={selectAll}>{t('buttons.selectAll', { ns: 'common' })}</Button>
              <Button variant="outline" size="sm" onClick={deselectAll}>{t('buttons.deselectAll', { ns: 'common' })}</Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {!role.product_id ? (
            <p className="text-muted-foreground text-sm py-4 text-center">
              {t('detail.noPermissionsForProduct')}
            </p>
          ) : (
            <>
              <div className="relative mb-4">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder={t('detail.filterPermissionsPlaceholder')}
                  value={permSearch}
                  onChange={(e) => setPermSearch(e.target.value)}
                  className="pl-10 h-8"
                />
              </div>

              {availablePerms.length === 0 ? (
                <p className="text-muted-foreground text-sm py-4 text-center">
                  {t('detail.noPermissionsForProduct')}
                </p>
              ) : (
                <div className="flex flex-col gap-4">
                  {Object.keys(domains).sort().map((domain) => (
                    <div key={domain}>
                      <div className="flex items-center gap-2 mb-1.5">
                        <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                          {domain}
                        </span>
                        <div className="flex-1 border-t border-dashed" />
                        <span className="text-xs text-muted-foreground">
                          {domains[domain].filter((p) => selectedPermIds.has(p.id)).length}/{domains[domain].length}
                        </span>
                      </div>
                      <div className="flex flex-col">
                        {domains[domain]
                          .sort((a, b) => a.code.localeCompare(b.code))
                          .map((perm) => {
                            const isSelected = selectedPermIds.has(perm.id)
                            const parts = perm.code.split('.')
                            const action = parts.slice(2).join('.') || parts[parts.length - 1]
                            const isWrite = action.includes('write') || action.includes('delete') || action.includes('execute')
                            const isExport = action.includes('export')
                            return (
                              <label
                                key={perm.id}
                                className={`flex items-center gap-3 py-2 px-3 rounded-md cursor-pointer transition-colors ${
                                  isSelected ? 'bg-primary/5 hover:bg-primary/10' : 'hover:bg-muted/40'
                                }`}
                              >
                                <div
                                  className={`flex items-center justify-center h-4 w-4 rounded border shrink-0 transition-colors ${
                                    isSelected
                                      ? 'bg-primary border-primary text-primary-foreground'
                                      : 'border-muted-foreground/30'
                                  }`}
                                >
                                  {isSelected && <Check className="h-3 w-3" />}
                                </div>
                                <input
                                  type="checkbox"
                                  checked={isSelected}
                                  onChange={() => togglePermission(perm.id)}
                                  className="sr-only"
                                />
                                <div className="flex flex-col gap-0.5 min-w-0 flex-1">
                                  <code className="text-xs font-mono">{perm.code}</code>
                                  {perm.description && (
                                    <span className="text-xs text-muted-foreground">{perm.description}</span>
                                  )}
                                </div>
                                <Badge
                                  variant={isWrite ? 'destructive' : isExport ? 'secondary' : 'outline'}
                                  className="text-[10px] shrink-0"
                                >
                                  {isWrite ? t('permissionTypes.write', { ns: 'common' }) : isExport ? t('permissionTypes.export', { ns: 'common' }) : t('permissionTypes.read', { ns: 'common' })}
                                </Badge>
                              </label>
                            )
                          })}
                      </div>
                    </div>
                  ))}
                  {filteredPerms.length === 0 && (
                    <p className="text-center text-muted-foreground py-4 text-sm">{t('detail.noPermissionsMatchFilter')}</p>
                  )}
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Delete confirmation */}
      <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{t('detail.deleteRole')}</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">
            {t('detail.deleteConfirm', { name: role.name })}
          </p>
          <div className="flex justify-end gap-2 mt-4">
            <Button variant="outline" onClick={() => setDeleteOpen(false)}>{t('buttons.cancel', { ns: 'common' })}</Button>
            <Button
              variant="destructive"
              onClick={() => deleteMutation.mutate()}
              disabled={deleteMutation.isPending}
            >
              {t('buttons.delete', { ns: 'common' })}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  )
}
